import { fileService } from "@/services/ServiceRegistry";
import { ReservationFormData } from "../guests/utils/reservationSchema";
import {
  CheckinStatus,
  CheckoutStatus,
  CreateReservationRequest,
} from "@/types/reservation";
import { useCreateReservation } from "@/hooks/useReservations";
import { useDocumentCreate } from "@/hooks/useDocumentCreate";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

export const useReservationCreate = () => {
  const navigate = useNavigate();
  const createReservationMutation = useCreateReservation();
  const { handleDocumentCreate } = useDocumentCreate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReservationCreate = async (
    data: ReservationFormData,
    hotelId: string,
    selectedPhotoFile: File | null,
  ) => {
    try {
      setIsLoading(true);
      const {
        firstName,
        lastName,
        language,
        phone,
        email,
        dateOfBirth,
        comments,
        documents,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        photoId,
        arrivalDate,
        departureDate,
        additionalGuests,
        ...bookingData
      } = data;

      let photoFileId = null;

      if (selectedPhotoFile) {
        try {
          photoFileId =
            await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
        } catch (photoError) {
          console.error("Failed to upload photo:", photoError);
        }
      }

      const arrivalWithTime = new Date(
        new Date(arrivalDate).setHours(14, 0, 0, 0),
      );
      const departureWithTime = new Date(
        new Date(departureDate).setHours(12, 0, 0, 0),
      );

      const guestDateOfBirth = new Date(dateOfBirth).toISOString();

      // Prepare reservation request
      const reservationRequest: CreateReservationRequest = {
        ...bookingData,
        hotelId: hotelId!,
        additionalGuests: (additionalGuests || []).map((g) => ({
          guestType: g.guestType,
          firstName: g.firstName,
          lastName: g.lastName,
          email: g.email,
          phone: g.phone,
          language: g.language,
          dateOfBirth: g.dateOfBirth,
        })),
        checkinStatus: CheckinStatus.NOT_DONE,
        checkoutStatus: CheckoutStatus.NOT_DONE,
        checkinMethod: "FLIGHT",
        estimatedTimeOfArrival: data.arrivalDate,
        preCheckinEnteredGuests: 0,
        checkinComments: comments || "",
        arrivalDate: arrivalWithTime.toISOString(),
        departureDate: departureWithTime.toISOString(),
        notes: [],
        guest: {
          photoId: photoFileId,
          firstName,
          lastName,
          email,
          phone,
          address: "",
          hotelId: hotelId!,
          dateOfBirth: guestDateOfBirth,
          language,
          preferences: {},
        },
      };

      // Create the reservation using the service
      const booking =
        await createReservationMutation.mutateAsync(reservationRequest);

      await Promise.all(
        (documents || []).map(async (document) => {
          if (!document.file) {
            console.error("Missing bookingId ID or file");
            return;
          }

          await handleDocumentCreate({
            ...document,
            file: document.file!,
            guestId: booking.guest.id,
            bookingId: booking.id,
          });
        }),
      );

      // Navigate back to reservations list
      navigate("/reservations");
    } catch (error) {
      toast.error((error as Error).message);
      setError((error as Error).message);
      console.error("Error creating reservation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleReservationSubmit: handleReservationCreate,
    isLoading: isLoading,
    error: error,
  };
};
